import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useHistory, useParams } from "react-router";
import ServiceWrapper from "../../components/ServiceWrapper";
import { NoteStage } from "../Notes";
import { INoteData } from "../../components/NotePlayer/types";
import { api } from "../../utils/api";
import { track } from "../../utils/track";
import { C, Page, Lead, Primary, Ghost, Err, Note, Sheet, Option, errText } from "./styles";
import { statusChip } from "./List";
import FixSheet from "./FixSheet";

export interface IMyInfo {
  status: "queued" | "processing" | "done" | "failed";
  error?: string | null;
  visibility: "private" | "link" | "group";
  is_owner: boolean;
  owner_nickname?: string;
  share_token?: string | null;
  group_ids: number[];
}

type IMyData = INoteData & { my: IMyInfo };

interface IGroup { id: number; name: string; member_count: number; is_owner: boolean }

const shareUrl = (token: string) => `${window.location.origin}/my-scores/s/${token}`;

/** 내 악보 보기 (/my-scores/:id, 공유 링크 /my-scores/s/:token) */
const MyScoreView: React.FC = () => {
  const history = useHistory();
  const { id, token } = useParams<{ id?: string; token?: string }>();
  const [data, setData] = useState<IMyData | null>(null);
  const [err, setErr] = useState("");
  const [sheet, setSheet] = useState<"" | "share" | "more" | "fix">("");

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(token ? `/my-scores/shared/${token}` : `/my-scores/${id}`);
      setData({ ...res.data, personal: true });
      setErr("");
    } catch (e) {
      setErr(errText(e, "악보를 불러오지 못했어요."));
    }
  }, [id, token]);
  useEffect(() => { load(); }, [load]);

  const waiting = !!data && (data.my.status === "queued" || data.my.status === "processing");
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [waiting, load]);

  const remove = async () => {
    if (!data || !window.confirm(`「${data.info!.title}」 악보를 지울까요? 지우면 되돌릴 수 없어요.`)) return;
    try {
      await api.delete(`/my-scores/${data.info!.id}`);
      history.replace("/my-scores");
    } catch (e) {
      window.alert(errText(e));
    }
  };

  if (err || !data || data.my.status !== "done") {
    return (
      <ServiceWrapper isFooterRendered={false}>
        <Page>
          {err ? (
            <>
              <h1>내 악보</h1>
              <Err>{err}</Err>
              <Ghost type="button" style={{ marginTop: 16 }} onClick={() => history.push("/my-scores")}>내 악보 목록</Ghost>
            </>
          ) : !data ? (
            <Lead>불러오는 중…</Lead>
          ) : (
            <>
              <h1>{data.info!.title}</h1>
              <StatusBox>
                {statusChip(data.my.status)}
                {data.my.status === "failed" ? (
                  <>
                    <p>{data.my.error || "악보를 만들지 못했어요."}</p>
                    <Note>만들지 못한 곡은 한도에서 빠져요. 다른 음원으로 다시 올려 주세요.</Note>
                    <Primary type="button" onClick={() => history.push("/my-scores/new")}>다시 올리기</Primary>
                  </>
                ) : (
                  <>
                    <p>{data.my.status === "processing" ? "AI 가 악보를 만들고 있어요." : "차례를 기다리고 있어요."}</p>
                    <Note>보통 10분 안에 완성돼요(사람이 많으면 더 걸릴 수 있어요). 이 화면을 닫아도 다 되면 알림으로 알려 드려요.</Note>
                  </>
                )}
                {data.my.is_owner && <Ghost type="button" className="danger" onClick={remove}>지우기</Ghost>}
              </StatusBox>
            </>
          )}
        </Page>
      </ServiceWrapper>
    );
  }

  const my = data.my;
  // 가사가 듬성듬성하면(알아듣기 어려운 녹음 → 뜻이 안 통하는 줄은 비워 둠) 안내 (2026-10-09 사장님)
  const rows = (data.musics || []) as any[];
  const withLyrics = rows.filter((m) => [1, 2, 3, 4].some((k) => (m[`lyrics_${k}`] || "").trim())).length;
  const sparse = my.is_owner && rows.length >= 8 && withLyrics > 0 && withLyrics / rows.length < 0.5;
  const top = (
    <>
    <TopBar>
      <button type="button" className="back" aria-label="내 악보 목록" onClick={() => history.push("/my-scores")}>
        <svg viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6" /></svg>
        내 악보
      </button>
      {my.is_owner ? (
        <div>
          <Ghost type="button" onClick={() => { track("myscore:fix-open"); setSheet("fix"); }}>수정 요청</Ghost>
          <Ghost type="button" onClick={() => setSheet("share")}>{my.visibility === "private" ? "공유" : my.visibility === "link" ? "링크 공유 중" : "단체 공유 중"}</Ghost>
          <Ghost type="button" aria-label="더 보기" onClick={() => setSheet("more")}>⋯</Ghost>
        </div>
      ) : (
        <span>{my.owner_nickname ? `${my.owner_nickname} 님이 공유한 악보` : "공유받은 악보"}</span>
      )}
    </TopBar>
    {sparse && (
      <SparseNote>
        알아듣기 어려운 부분의 가사는 비워 두었어요.{" "}
        <button type="button" onClick={() => setSheet("fix")}>수정 요청</button>으로 알려 주시면 채워 드려요.
      </SparseNote>
    )}
    </>
  );

  return (
    <>
      <NoteStage noteData={data} top={top} />
      {sheet === "share" && (
        <ShareSheet
          scoreId={data.info!.id}
          my={my}
          onClose={() => setSheet("")}
          onSaved={(m) => setData({ ...data, my: { ...my, ...m } })}
        />
      )}
      {sheet === "fix" && <FixSheet scoreId={data.info!.id} onClose={() => setSheet("")} />}
      {sheet === "more" && (
        <Sheet onClick={() => setSheet("")}>
          <div onClick={(e) => e.stopPropagation()}>
            <h2>{data.info!.title}</h2>
            <Option type="button" onClick={() => { track("myscore:edit"); history.push(`/my-scores/${data.info!.id}/edit`); }}><b>직접 고치기</b><span>코드·가사를 한 칸씩 직접 바꿔요.</span></Option>
            <Option type="button" onClick={remove}><b style={{ color: C.err }}>악보 지우기</b><span>지우면 되돌릴 수 없어요. 공유한 사람들도 더 볼 수 없어요.</span></Option>
            <Ghost type="button" style={{ width: "100%" }} onClick={() => setSheet("")}>닫기</Ghost>
          </div>
        </Sheet>
      )}
    </>
  );
};

export default MyScoreView;

/** 공유 설정: 나만 보기 / 링크 / 단체 */
const ShareSheet: React.FC<{ scoreId: number; my: IMyInfo; onClose: () => void; onSaved: (m: Partial<IMyInfo>) => void }> = ({ scoreId, my, onClose, onSaved }) => {
  const history = useHistory();
  const [vis, setVis] = useState(my.visibility);
  const [groups, setGroups] = useState<IGroup[] | null>(null);
  const [picked, setPicked] = useState<number[]>(my.group_ids || []);
  const [token, setToken] = useState(my.share_token || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/my-score-groups").then(({ data }) => setGroups(data.data)).catch(() => setGroups([]));
  }, []);

  const save = async (v = vis) => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post(`/my-scores/${scoreId}/share`, { visibility: v, group_ids: picked });
      setToken(data.data.share_token || "");
      onSaved({ visibility: data.data.visibility, share_token: data.data.share_token, group_ids: data.data.group_ids });
      track(`myscore:share-${v}`);
      setMsg(v === "private" ? "이제 나만 볼 수 있어요." : v === "link" ? "링크를 만들었어요. 복사해서 보내 주세요." : "단체에 공유했어요.");
    } catch (e) {
      setMsg(errText(e));
    }
    setBusy(false);
  };

  const copy = async () => {
    const url = shareUrl(token);
    try {
      if ((navigator as any).share) await (navigator as any).share({ title: "코드스코어 악보", url });
      else { await navigator.clipboard.writeText(url); setMsg("링크를 복사했어요."); }
    } catch (e) {
      try { await navigator.clipboard.writeText(url); setMsg("링크를 복사했어요."); } catch (_) { window.prompt("이 링크를 복사해 주세요", url); }
    }
  };

  return (
    <Sheet onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}>
        <h2>누구와 볼까요?</h2>
        <Option type="button" on={vis === "private"} onClick={() => setVis("private")}><b>나만 보기</b><span>나만 볼 수 있어요.</span></Option>
        <Option type="button" on={vis === "link"} onClick={() => setVis("link")}><b>링크로 공유</b><span>링크를 받은 사람이 코드스코어에 로그인하면 볼 수 있어요.</span></Option>
        <Option type="button" on={vis === "group"} onClick={() => setVis("group")}><b>단체에 공유</b><span>밴드·찬양팀처럼 단체 회원만 볼 수 있어요.</span></Option>

        {vis === "group" && (
          <GroupPick>
            {groups === null && <Note>단체 불러오는 중…</Note>}
            {groups && !groups.length && <Note>아직 들어간 단체가 없어요.</Note>}
            {(groups || []).map((g) => (
              <label key={g.id}>
                <input
                  type="checkbox"
                  checked={picked.includes(g.id)}
                  onChange={(e) => setPicked(e.target.checked ? [...picked, g.id] : picked.filter((x) => x !== g.id))}
                />
                <span>{g.name} <em>{g.member_count}명</em></span>
              </label>
            ))}
            <button type="button" onClick={() => history.push("/my-scores/groups")}>단체 만들기·초대 코드로 들어가기</button>
          </GroupPick>
        )}

        <Primary type="button" disabled={busy || (vis === "group" && !picked.length)} onClick={() => save()}>
          {busy ? "저장하는 중…" : "이대로 저장"}
        </Primary>
        {vis === "link" && my.visibility === "link" && token && (
          <LinkRow>
            <code>{shareUrl(token)}</code>
            <Ghost type="button" onClick={copy}>링크 보내기</Ghost>
          </LinkRow>
        )}
        {msg && <Note style={{ marginTop: 10, color: C.muted }}>{msg}</Note>}
        <Ghost type="button" style={{ width: "100%", marginTop: 12 }} onClick={onClose}>닫기</Ghost>
      </div>
    </Sheet>
  );
};

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 12px;
  color: ${C.fg};
  & > .back {
    display: flex;
    align-items: center;
    gap: 2px;
    border: 0;
    background: none;
    color: ${C.muted};
    font-size: 15px;
    cursor: pointer;
    padding: 6px 0;
    white-space: nowrap;
  }
  & > .back > svg {
    width: 20px;
    height: 20px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
  }
  & > div {
    display: flex;
    gap: 6px;
    min-width: 0;
  }
  & > div > button {
    height: 38px;
    padding: 0 12px;
    font-size: 14px;
  }
  & > span {
    font-size: 13px;
    color: ${C.dim};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StatusBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  margin-top: 16px;
  & > p {
    margin: 0;
    font-size: 16px;
    line-height: 1.6;
  }
  & > button {
    width: 100%;
  }
`;

const GroupPick = styled.div`
  margin: 4px 0 14px;
  padding: 12px 14px;
  border-radius: 12px;
  background: ${C.card};
  & > label {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
    font-size: 15px;
    cursor: pointer;
  }
  & > label > input {
    width: 20px;
    height: 20px;
    accent-color: ${C.accent};
  }
  & em {
    font-style: normal;
    font-size: 13px;
    color: ${C.dim};
  }
  & > button {
    border: 0;
    background: none;
    padding: 8px 0 0;
    color: ${C.link};
    font-size: 14px;
    cursor: pointer;
  }
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  & > code {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    color: ${C.muted};
  }
`;

const SparseNote = styled.p`
  margin: 10px 0 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${C.card};
  color: ${C.muted};
  font-size: 13px;
  line-height: 1.5;
  & > button {
    border: 0;
    background: none;
    padding: 0;
    color: ${C.link};
    font-size: 13px;
    text-decoration: underline;
    cursor: pointer;
  }
`;
